import React, { useEffect, useMemo, useState } from "react";
import RazorpayButton from "./RazorpayButton";
import { auth } from "./firebase";
import { ToneBadge } from "./petaarUi";

const VET_AVATAR =
  "https://images.pexels.com/photos/6234601/pexels-photo-6234601.jpeg?auto=compress&cs=tinysrgb&fit=crop&w=520&h=520";
const PET_GROUP_IMAGE =
  "https://images.pexels.com/photos/406014/pexels-photo-406014.jpeg?auto=compress&cs=tinysrgb&fit=crop&w=1000&h=700";

const INCLUDED_VET_SERVICES = [
  {
    name: "Home Vet Visits",
    subtitle: "Available today",
    icon: "https://api.iconify.design/mdi/home-heart.svg?color=%231d4ed8&width=72&height=72",
  },
  {
    name: "Emergency Vet Service",
    subtitle: "Urgent cases",
    icon: "https://api.iconify.design/mdi/ambulance.svg?color=%231d4ed8&width=72&height=72",
  },
  {
    name: "Vaccination Service",
    subtitle: "Preventive care",
    icon: "https://api.iconify.design/mdi/needle.svg?color=%231d4ed8&width=72&height=72",
  },
  {
    name: "Pet Testing",
    subtitle: "Book any day",
    icon: "https://api.iconify.design/mdi/test-tube.svg?color=%231d4ed8&width=72&height=72",
  },
  {
    name: "Pet DNA Testing",
    subtitle: "Breed & health",
    icon: "https://api.iconify.design/mdi/dna.svg?color=%231d4ed8&width=72&height=72",
  },
  {
    name: "Pet Physiotherapy",
    subtitle: "Best for rehab",
    icon: "https://api.iconify.design/mdi/dumbbell.svg?color=%231d4ed8&width=72&height=72",
  },
  {
    name: "Pet Rehabilitation",
    subtitle: "Recovery support",
    icon: "https://api.iconify.design/mdi/heart-pulse.svg?color=%231d4ed8&width=72&height=72",
  },
  {
    name: "Pet Boarding for Medical Care",
    subtitle: "Monitored stay",
    icon: "https://api.iconify.design/mdi/hospital-box-outline.svg?color=%231d4ed8&width=72&height=72",
  },
  {
    name: "Pet Microchipping",
    subtitle: "Secure identity",
    icon: "https://api.iconify.design/mdi/chip.svg?color=%231d4ed8&width=72&height=72",
  },
];

const HOW_IT_WORKS = [
  "Pick a slot",
  "Pay securely",
  "Video consult",
  "Get prescription/report",
];
const TEMP_CONSULT_PRICE = 1;
const SUPPORTED_CONSULT_MODES = ["chat", "audio", "video"];
const CONSULT_MODE_LABELS = {
  chat: "Chat",
  audio: "Audio Call",
  video: "Video Call",
};
const VET_OPS_URL =
  process.env.REACT_APP_VET_OPS_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/vetOps";

function normalizeConsultationModeValue(modeValue = "") {
  const mode = String(modeValue || "").trim().toLowerCase();
  return SUPPORTED_CONSULT_MODES.includes(mode) ? mode : "";
}

function buildConsultationTopicByMode(modeValue = "") {
  const mode = normalizeConsultationModeValue(modeValue);
  if (mode === "audio") return "Instant audio vet consultation";
  if (mode === "video") return "Instant video vet consultation";
  return "Instant chat vet consultation";
}

export default function InstantVetConsultation({ selectedPet }) {
  const [avatarSrc, setAvatarSrc] = useState(VET_AVATAR);
  const [petsImageSrc, setPetsImageSrc] = useState(PET_GROUP_IMAGE);
  const [onlineVets, setOnlineVets] = useState([]);
  const [onlineVetsLoading, setOnlineVetsLoading] = useState(true);
  const [onlineVetsError, setOnlineVetsError] = useState("");
  const [selectedVetId, setSelectedVetId] = useState("");
  const [selectedConsultationMode, setSelectedConsultationMode] = useState("chat");
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadOnlineVets = async () => {
      setOnlineVetsLoading(true);
      setOnlineVetsError("");
      try {
        let idToken = await auth.currentUser?.getIdToken();
        if (!idToken) {
          await new Promise((resolve) => setTimeout(resolve, 400));
          idToken = await auth.currentUser?.getIdToken();
        }
        if (!idToken) {
          throw new Error("Please login again to view online vets.");
        }
        const response = await fetch(VET_OPS_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ action: "list_online_vets" }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload?.success) {
          throw new Error(payload?.error || `Unable to load online vets (${response.status})`);
        }
        const vets = Array.isArray(payload.vets) ? payload.vets : [];
        const rows = vets
          .map((item) => ({
            providerId: String(item.providerId || "").trim(),
            providerEmail: String(item.providerEmail || "").trim().toLowerCase(),
            displayName: String(item.displayName || "Verified Petaar Vet").trim(),
            yearsOfService: Math.max(0, Number(item.yearsOfService) || 0),
            location: String(item.location || "").trim(),
            currentWorkplace: String(item.currentWorkplace || "").trim(),
            profilePhotoUrl: String(item.profilePhotoUrl || "").trim(),
            consultationModes: Array.isArray(item.consultationModes) ?
              item.consultationModes.map((mode) => String(mode || "").trim()).filter(Boolean).slice(0, 5) :
              [],
            languages: Array.isArray(item.languages) ?
              item.languages.map((lang) => String(lang || "").trim()).filter(Boolean).slice(0, 8) :
              [],
            specializations: Array.isArray(item.specializations) ?
              item.specializations.map((spec) => String(spec || "").trim()).filter(Boolean).slice(0, 8) :
              [],
          }))
          .filter((item) => item.providerId && item.providerEmail);

        if (cancelled) return;
        setOnlineVets(rows);
      } catch (error) {
        if (cancelled) return;
        setOnlineVets([]);
        setOnlineVetsError(error?.message || "Unable to load online vets.");
      } finally {
        if (!cancelled) {
          setOnlineVetsLoading(false);
        }
      }
    };
    loadOnlineVets();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!onlineVets.length) {
      setSelectedVetId("");
      return;
    }
    if (!onlineVets.some((item) => item.providerId === selectedVetId)) {
      setSelectedVetId(onlineVets[0].providerId);
    }
  }, [onlineVets, selectedVetId]);

  const selectedVet = useMemo(
    () => onlineVets.find((item) => item.providerId === selectedVetId) || null,
    [onlineVets, selectedVetId],
  );
  const availableConsultModes = useMemo(() => {
    const sourceModes = Array.isArray(selectedVet?.consultationModes) ? selectedVet.consultationModes : [];
    const normalized = sourceModes
      .map((item) => normalizeConsultationModeValue(item))
      .filter(Boolean);
    const unique = [...new Set(normalized)];
    return unique.length ? unique : SUPPORTED_CONSULT_MODES;
  }, [selectedVet]);

  useEffect(() => {
    if (!availableConsultModes.includes(selectedConsultationMode)) {
      setSelectedConsultationMode(availableConsultModes[0] || "chat");
    }
  }, [availableConsultModes, selectedConsultationMode]);

  const isMobile = viewportWidth < 760;
  const isTablet = viewportWidth < 1120;
  const selectedModeLabel = CONSULT_MODE_LABELS[selectedConsultationMode] || "Consult";
  const selectedModeTopic = buildConsultationTopicByMode(selectedConsultationMode);
  const consultationService = {
    id: "instant-vet-video-consultation",
    title: "Instant Video Vet Consultation",
    category: "Veterinary & Health Care",
    city: "",
    providerId: "",
    providerEmail: "",
    price: TEMP_CONSULT_PRICE,
  };

  const manualPetDetails = {
    name: selectedPet?.name || "",
    species: selectedPet?.species || "",
    breed: selectedPet?.breed || "",
    age: selectedPet?.age || "",
    weight: selectedPet?.weight || "",
    symptoms: "",
    notes: "",
  };

  return (
    <div
      className="petaar-card"
      style={{
        marginTop: 14,
        padding: isMobile ? "18px 14px" : "24px 20px",
        background:
          "radial-gradient(circle at 88% 72%, rgba(191,219,254,0.38) 0%, rgba(255,255,255,0) 42%), linear-gradient(135deg, #ffffff 0%, #eef4ff 100%)",
        display: "grid",
        gap: 18,
        width: "100%",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isTablet ? "1fr" : "minmax(0, 1.8fr) minmax(280px, 1fr)",
          gap: 12,
        }}
      >
        <div className="petaar-card" style={{ padding: isMobile ? 16 : 20 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "auto minmax(0, 1fr) auto",
              gap: 10,
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <img
                src={avatarSrc}
                alt="Vet profile"
                onError={() => setAvatarSrc("/petaar-logo.jpeg")}
                style={{
                  width: isMobile ? 64 : 78,
                  height: isMobile ? 64 : 78,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "2px solid #dbeafe",
                  boxShadow: "0 8px 16px rgba(59, 130, 246, 0.22)",
                  flexShrink: 0,
                }}
              />
              <div>
                <div className="petaar-eyebrow" style={{ marginBottom: 10 }}>Consult Vet</div>
                <div style={{ fontSize: isMobile ? 28 : 38, fontWeight: 800, color: "#1e3a8a", lineHeight: 1.08 }}>
                  Secure online vet consultation
                </div>
                <div style={{ marginTop: 4, color: "#334155", fontWeight: 600, fontSize: isMobile ? 16 : 18 }}>
                  Licensed vets · Avg response &lt; 5 min · Secure payment
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            {["Verified vets", "24/7 Support", "Secure", "4.8+ Avg rating"].map((item) => (
              <span
                key={item}
                className="petaar-chip"
                style={{ fontSize: isMobile ? 13 : 14 }}
              >
                {item}
              </span>
            ))}
          </div>

          <div
            style={{
              marginTop: 12,
              border: "1px solid #dbeafe",
              borderRadius: 12,
              background: "#f8fbff",
              padding: isMobile ? 10 : 12,
            }}
          >
            <div style={{ fontSize: isMobile ? 19 : 24, fontWeight: 800, color: "#1e3a8a", marginBottom: 8 }}>
              Online vets now {onlineVets.length ? `(${onlineVets.length})` : ""}
            </div>
            {onlineVetsLoading && (
              <div style={{ color: "#475569", fontWeight: 600 }}>Loading online vets...</div>
            )}
            {!onlineVetsLoading && onlineVetsError && (
              <div style={{ color: "#b91c1c", fontWeight: 700 }}>{onlineVetsError}</div>
            )}
            {!onlineVetsLoading && !onlineVetsError && !onlineVets.length && (
              <div style={{ color: "#475569", fontWeight: 600 }}>
                No vets are online right now. Please try again in a few minutes.
              </div>
            )}
            {!onlineVetsLoading && !onlineVetsError && onlineVets.length > 0 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(240px, 1fr))",
                  gap: 8,
                }}
              >
                {onlineVets.map((vet) => {
                  const selected = vet.providerId === selectedVetId;
                  return (
                    <button
                      key={vet.providerId}
                      type="button"
                      onClick={() => setSelectedVetId(vet.providerId)}
                    style={{
                        border: selected ? "2px solid #1d4ed8" : "1px solid #cbd5e1",
                        background: selected ? "#eff6ff" : "#ffffff",
                        borderRadius: 16,
                        textAlign: "left",
                        padding: "14px 14px",
                        cursor: "pointer",
                        boxShadow: selected ? "0 8px 18px rgba(37, 99, 235, 0.18)" : "none",
                      }}
                    >
                      <div style={{ fontWeight: 800, color: "#0f172a", fontSize: 15 }}>{vet.displayName}</div>
                      <div style={{ color: "#475569", fontSize: 13, marginTop: 4 }}>
                        {vet.location || vet.currentWorkplace || "India"}
                      </div>
                      <div style={{ color: "#1e3a8a", fontSize: 12, marginTop: 6, fontWeight: 700 }}>
                        {vet.yearsOfService ? `${vet.yearsOfService}+ years` : "Verified"}
                        {vet.consultationModes.length ? ` • ${vet.consultationModes.join(", ")}` : ""}
                      </div>
                      {vet.languages.length > 0 && (
                        <div style={{ color: "#334155", fontSize: 12, marginTop: 4 }}>
                          Languages: {vet.languages.join(", ")}
                        </div>
                      )}
                      <div style={{ marginTop: 10 }}>
                        <ToneBadge tone={selected ? "success" : "default"}>
                          {selected ? "Selected vet" : "Click to select"}
                        </ToneBadge>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ marginTop: 10 }}>
            <div style={{ color: "#1e3a8a", fontWeight: 800, fontSize: 14, marginBottom: 6 }}>
              Choose consultation mode
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {availableConsultModes.map((mode) => {
                const active = selectedConsultationMode === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setSelectedConsultationMode(mode)}
                    className={active ? "petaar-button" : "petaar-button--secondary"}
                    style={{ minHeight: 36, padding: "8px 14px", fontSize: 13 }}
                  >
                    {CONSULT_MODE_LABELS[mode] || mode}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            style={{
              marginTop: 10,
              display: "flex",
              gap: 10,
              flexWrap: "nowrap",
              alignItems: "center",
              overflowX: "auto",
              paddingBottom: 2,
            }}
          >
            <RazorpayButton
              service={consultationService}
              selectedPet={selectedPet}
              manualPetDetails={manualPetDetails}
              bookingType="vet_consultation"
              consultationMode={selectedConsultationMode}
              consultationTopic={selectedModeTopic}
              requestedLanguage=""
              preferredProviderId={selectedVet?.providerId || ""}
              preferredProviderEmail={selectedVet?.providerEmail || ""}
              disableDateSelection
              buttonLabel={
                selectedVet ?
                  `Book ${selectedModeLabel} with ${selectedVet.displayName}` :
                  `Book ${selectedModeLabel}`
              }
              containerStyle={{ display: "flex", justifyContent: "flex-start", flexWrap: "nowrap" }}
              buttonStyle={{
                border: "none",
                borderRadius: 999,
                padding: isMobile ? "11px 20px" : "12px 24px",
                fontWeight: 800,
                fontSize: isMobile ? 18 : 22,
                color: "#fff",
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 70%, #312e81 100%)",
                boxShadow: "0 10px 20px rgba(29, 78, 216, 0.32)",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            />
            <span
              className="petaar-badge petaar-badge--accent"
              style={{
                padding: isMobile ? "10px 16px" : "12px 18px",
                fontWeight: 900,
                fontSize: isMobile ? 24 : 28,
                whiteSpace: "nowrap",
              }}
            >
              ₹{TEMP_CONSULT_PRICE}
            </span>
          </div>

          <div style={{ borderTop: "1px solid #dbeafe", marginTop: 12, paddingTop: 10 }}>
            <div style={{ fontSize: 30, fontWeight: 800, color: "#1e3a8a", marginBottom: 10 }}>
              Other services
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? 220 : 250}px, 1fr))`,
                gap: 10,
              }}
            >
              {INCLUDED_VET_SERVICES.map((item) => (
                <div
                  key={item.name}
                  style={{
                    border: "1px solid #dbeafe",
                    background: "#ffffff",
                    borderRadius: 12,
                    padding: "10px 10px",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 999,
                      background: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={item.icon}
                      alt={`${item.name} icon`}
                      style={{ width: 24, height: 24, objectFit: "contain" }}
                    />
                  </span>
                  <div>
                    <div style={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.2, fontSize: 17 }}>{item.name}</div>
                    <div style={{ fontSize: 14, color: "#64748b", marginTop: 2 }}>{item.subtitle}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <aside className="petaar-card" style={{ padding: 16, display: "grid", gap: 12, alignContent: "start" }}>
          <img
            src={petsImageSrc}
            alt="Pets ready for consultation"
            onError={() => setPetsImageSrc("/petaar-logo.jpeg")}
            style={{ width: "100%", height: isMobile ? 180 : 220, objectFit: "cover", borderRadius: 12, border: "1px solid #dbeafe" }}
          />
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#1e3a8a", marginBottom: 8 }}>How it works</div>
            <div style={{ display: "grid", gap: 8 }}>
              {HOW_IT_WORKS.map((item) => (
                <div
                  key={item}
                  style={{
                    borderBottom: "1px solid #e2e8f0",
                    paddingBottom: 8,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: "#1e293b",
                    fontWeight: 700,
                    fontSize: 15,
                  }}
                >
                  <span
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 8,
                      background: "#eef2ff",
                      border: "1px solid #bfdbfe",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#1d4ed8",
                      fontWeight: 900,
                      fontSize: 14,
                    }}
                  >
                    ✓
                  </span>
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#1e3a8a", marginBottom: 8 }}>What you can ask</div>
            <div style={{ display: "grid", gap: 8 }}>
              {["Vomiting", "Skin issue", "Vaccination", "Diet", "All pet related issues"].map((item) => (
                <div
                  key={item}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    background: "#f8fbff",
                    padding: "8px 10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontWeight: 700,
                    color: "#0f172a",
                  }}
                >
                  <span>{item}</span>
                  <span style={{ color: "#1d4ed8" }}>↘</span>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
